import BreadCrumbs from 'components/breadcrumbs'
import { ResponsiveActionButton } from 'components/Buttons'
import CustomButton from 'components/Buttons/CustomButton'
import LihatDetail from 'components/Icons/LihatDetail'
import LoadingV2 from 'components/LoadingV2'
import PMBBadge from 'components/PMBBadge'
import PMBTabSwitcher from 'components/PMBTabSwitcher'
import PMBPagination from 'components/PMBPagination'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBSelect from 'components/PMBSelect'
import PMBTooltip from 'components/PMBTooltip'
import { PMBDateRangePicker } from 'components/PMBNewDatePicker'
import moment from 'moment-timezone'
import { TableComponent as DataTable } from 'components/TableComponent'
import { capitalizeEveryWord, useDebounce } from 'helpers'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import {
  ChevronDown,
  Info,
  Plus,
  Download,
  ChevronUp,
  Filter,
} from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Badge, Card, CardBody, Col, Row, Spinner, Collapse, Input } from 'reactstrap'
import { formatDateOnlyPMBV1 } from 'utils/formatDate'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import PMBProgressBarExportFile from 'components/PMBProgressBarExportFile'
import { URL_PROJECTS_ADD, URL_PROJECTS_MENU } from 'utils/pathUrl'
import ModalConfirmClose from './ModalConfirmClose'
import ModalConfirmDelete from './ModalConfirmDelete'
import ModalShareProject from './ModalShareProject'
import { handleDownloadExcel } from './downloadProject'
import {
  useDeleteProjectMutation,
  useFetchProjectsListQuery,
  useLazyFetchProjectsListQuery,
  useLazyFetchProjectByIdQuery,
  useUpdateProjectMenuMutation,
  useGetSharedProjectQuery,
  useLazyGetSharedProjectQuery,
  useUpdateProjectMenuSharedMutation,
  useLazySharedProjectDetailQuery,
  useListResourcesAccessQuery,
  useFetchFarmersProjectQuery,
} from './projectsMenuListApiSlice'
import { setFetchOption, setFetchOptionShared } from './projectsMenuListSlice'
import { getComodities } from './projectsAction'
import { P } from 'components'
import SvgIcon from 'components/Icon/SvgIcon'

const STATUS_OPTIONS = [
  { value: undefined, label: 'Semua' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'CLOSED', label: 'Closed' },
]

const COMPLETION_PROGRESS_OPTIONS = [
  { value: undefined, label: 'Semua' },
  { value: '1', label: 'Selesai' },
  { value: '0', label: 'Belum Selesai' },
]

const SharedProjectActions = ({ row, navigate, onClose }) => {
  const { profile } = useSelector((state) => state.dashboard)
  const { data: resourceAccessData } = useListResourcesAccessQuery(
    { resourceId: row?.id, resourceType: 'PROJECT' },
    { skip: !row?.id },
  )

  const getUserRole = () => {
    if (!profile?.userId || !resourceAccessData?.participants) return null
    const participant = resourceAccessData.participants.find(
      (p) => p.userId === profile.userId,
    )
    return participant?.role || null
  }

  const userRole = getUserRole()
  const isOwner = userRole === 'OWNER'
  const isEditor = userRole === 'EDITOR'
  const canEdit = isOwner || isEditor
  const isDraft = row?.status === 'DRAFT'

  return (
    <div
      className="d-flex gap-2 align-items-center"
      data-testid={`project-table-row-${row?.id}`}
    >
      <LihatDetail
        onClick={() => {
          navigate(`${URL_PROJECTS_MENU}/${row?.id}?shared=true`)
        }}
        id={row?.id}
      />
      {canEdit && (
        <ResponsiveActionButton
          disabled={!isDraft && row?.status !== 'ACTIVE'}
          iconName="pencil"
          label="Ubah"
          onClick={() => {
            navigate(`/projects/edit/${row?.id}?shared=true`)
          }}
        />
      )}
      {canEdit && (
        <ResponsiveActionButton
          disabled={isDraft || row?.status === 'CLOSED'}
          key="close"
          iconName="x"
          label="Tutup"
          onClick={() => onClose(row)}
        />
      )}
    </div>
  )
}



const ListProjectsMenu = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { tag, tagShared } = useSelector((state) => state.projectsMenuList)

  const searchParams = new URLSearchParams(location.search)
  const initialTab = searchParams.get('shared') === 'true' ? 'shared' : 'own'

  const [activeTab, setActiveTab] = useState(initialTab)
  const tabs = [
    { key: 'own', label: 'Project Saya' },
    { key: 'shared', label: 'Project Bersama' },
  ]

  const sharedTab = activeTab === 'shared'
  const activeTag = sharedTab ? tagShared : tag

  const dispatchActiveTag = (payload) => {
    dispatch(
      sharedTab ? setFetchOptionShared(payload) : setFetchOption(payload),
    )
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    const newTag = tab === 'shared' ? tagShared : tag
    setSearchInput(newTag?.s || '')
    setGoalMinInput(newTag?.goalMin || '')
    setGoalMaxInput(newTag?.goalMax || '')
    setCompletionMinInput(newTag?.completionMin || '')
    setCompletionMaxInput(newTag?.completionMax || '')

    const params = new URLSearchParams(location.search)

    if (tab === 'shared') {
      params.set('shared', 'true')
    } else {
      params.delete('shared')
    }

    navigate(`${location.pathname}${params.toString() ? `?${params}` : ''}`, {
      replace: true,
    })
  }

  // API for "Project Saya" tab
  const ownResult = useFetchProjectsListQuery(
    {
      sortby: tag?.sortby,
      sortdir: tag?.sortdir,
      search: tag?.s,
      page: tag?.page,
      size: tag?.size,
      status: tag?.status,
      from: tag?.from,
      to: tag?.to,
      workerUserIds: tag?.workerUserIds,
      targetIds: tag?.targetIds,
      goal: tag?.goal,
      goalMin: tag?.goalMin,
      goalMax: tag?.goalMax,
      completion: tag?.completion,
      completionMin: tag?.completionMin,
      completionMax: tag?.completionMax,
      completion_progress: tag?.completion_progress,
    },
    {
      skip: activeTab !== 'own',
      refetchOnMountOrArgChange: true,
    },
  )

  // API for "Project Shared" tab
  const sharedResult = useGetSharedProjectQuery(
    {
      sortby: tagShared?.sortby,
      sortdir: tagShared?.sortdir,
      search: tagShared?.s,
      page: tagShared?.page,
      size: tagShared?.size,
      status: tagShared?.status,
      from: tagShared?.from,
      to: tagShared?.to,
      workerUserIds: tagShared?.workerUserIds,
      targetIds: tagShared?.targetIds,
      goal: tagShared?.goal,
      goalMin: tagShared?.goalMin,
      goalMax: tagShared?.goalMax,
      completion: tagShared?.completion,
      completionMin: tagShared?.completionMin,
      completionMax: tagShared?.completionMax,
      completion_progress: tagShared?.completion_progress,
    },
    {
      skip: activeTab !== 'shared',
      refetchOnMountOrArgChange: true,
    },
  )

  const { data, isLoading, refetch, isFetching } =
    activeTab === 'shared' ? sharedResult : ownResult

  const { data: dataMember, isLoading: isLoadingMember } =
    useFetchFarmersProjectQuery({
      s: '',
      page: 0,
      size: 9999,
      sortby: 'createdAt',
      sortdir: 'DESC',
    })

  const memberOptions = useMemo(() => {
    return (
      dataMember?.items?.map((item) => ({
        value: item?.userId || item?.id,
        label: item?.name
          ? `${item.name}${item.phoneNumber ? ` (${item.phoneNumber})` : ''}`
          : item?.phoneNumber || '-',
      })) || []
    )
  }, [dataMember?.items])

  const selectedWorkers = useMemo(() => {
    if (!activeTag?.workerUserIds) return []
    const ids = Array.isArray(activeTag.workerUserIds)
      ? activeTag.workerUserIds
      : String(activeTag.workerUserIds).split(',').map((id) => id.trim())
    return memberOptions.filter((opt) =>
      ids.some((id) => String(id) === String(opt.value)),
    )
  }, [activeTag?.workerUserIds, memberOptions])

  const { comodities: listComodities } = useSelector(
    (state) => state.projectsMenuList,
  )

  useEffect(() => {
    if (!listComodities?.length) {
      dispatch(getComodities())
    }
  }, [dispatch, listComodities?.length])

  const targetOptions = useMemo(() => {
    return (
      listComodities?.map((item) => ({
        value: item?.id ?? item?.value,
        label: item?.label ?? item?.nameItem,
        image: item?.image ?? item?.img,
      })) || []
    )
  }, [listComodities])

  const selectedTargets = useMemo(() => {
    if (!activeTag?.targetIds) return []
    const ids = Array.isArray(activeTag.targetIds)
      ? activeTag.targetIds
      : String(activeTag.targetIds).split(',').map((id) => id.trim())
    return targetOptions.filter((opt) =>
      ids.some((id) => String(id) === String(opt.value)),
    )
  }, [activeTag?.targetIds, targetOptions])

  const [fetchProjectById, res] = useLazyFetchProjectByIdQuery()
  const [fetchSharedProjectById, sharedRes] = useLazySharedProjectDetailQuery()

  const [searchInput, setSearchInput] = useState(activeTag?.s || '')
  const [goalMinInput, setGoalMinInput] = useState(activeTag?.goalMin || '')
  const [goalMaxInput, setGoalMaxInput] = useState(activeTag?.goalMax || '')
  const [completionMinInput, setCompletionMinInput] = useState(
    activeTag?.completionMin || '',
  )
  const [completionMaxInput, setCompletionMaxInput] = useState(
    activeTag?.completionMax || '',
  )
  const [isShowFilter, setIsShowFilter] = useState(false)

  useEffect(() => {
    setGoalMinInput(activeTag?.goalMin || '')
  }, [activeTag?.goalMin])

  useEffect(() => {
    setGoalMaxInput(activeTag?.goalMax || '')
  }, [activeTag?.goalMax])

  useEffect(() => {
    setCompletionMinInput(activeTag?.completionMin || '')
  }, [activeTag?.completionMin])

  useEffect(() => {
    setCompletionMaxInput(activeTag?.completionMax || '')
  }, [activeTag?.completionMax])

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (activeTag?.from && activeTag?.to) count++
    if (activeTag?.status) count++
    if (
      activeTag?.workerUserIds &&
      (Array.isArray(activeTag.workerUserIds)
        ? activeTag.workerUserIds.length > 0
        : !!activeTag.workerUserIds)
    ) {
      count++
    }
    if (
      activeTag?.targetIds &&
      (Array.isArray(activeTag.targetIds)
        ? activeTag.targetIds.length > 0
        : !!activeTag.targetIds)
    ) {
      count++
    }
    if (activeTag?.goalMin || activeTag?.goalMax || activeTag?.goal?.trim()) {
      count++
    }
    if (
      activeTag?.completionMin ||
      activeTag?.completionMax ||
      activeTag?.completion?.trim()
    ) {
      count++
    }
    if (
      activeTag?.completion_progress !== undefined &&
      activeTag?.completion_progress !== null &&
      activeTag?.completion_progress !== ''
    ) {
      count++
    }
    if (searchInput?.trim()) count++
    return count
  }, [
    activeTag?.from,
    activeTag?.to,
    activeTag?.status,
    activeTag?.workerUserIds,
    activeTag?.targetIds,
    activeTag?.goal,
    activeTag?.goalMin,
    activeTag?.goalMax,
    activeTag?.completion,
    activeTag?.completionMin,
    activeTag?.completionMax,
    activeTag?.completion_progress,
    searchInput,
  ])

  useEffect(() => {
    if (activeFilterCount > 0) setIsShowFilter(true)
  }, [activeFilterCount])

  const handleResetFilter = () => {
    setSearchInput('')
    setGoalMinInput('')
    setGoalMaxInput('')
    setCompletionMinInput('')
    setCompletionMaxInput('')
    dispatchActiveTag({
      s: undefined,
      from: undefined,
      to: undefined,
      status: undefined,
      workerUserIds: undefined,
      targetIds: undefined,
      goal: undefined,
      goalMin: undefined,
      goalMax: undefined,
      completion: undefined,
      completionMin: undefined,
      completionMax: undefined,
      completion_progress: undefined,
      page: 0,
    })
  }

  const [isOpenDelete, setIsOpenDelete] = useState(false)
  const [deleteProjectId, setDeleteProjectId] = useState(null)
  const [deleteProjectLabel, setDeleteProjectLabel] = useState('')
  const [deleteErrMsg, setDeleteErrMsg] = useState('')
  const [isOpenClose, setIsOpenClose] = useState(false)
  const [closeProjectId, setCloseProjectId] = useState(null)
  const [closeProjectLabel, setCloseProjectLabel] = useState('')
  const [closeProjectRow, setCloseProjectRow] = useState(null)
  const [closeErrMsg, setCloseErrMsg] = useState('')
  const [isOpenShare, setIsOpenShare] = useState(false)
  const [shareProject, setShareProject] = useState(null)
  const [shareParticipants, setShareParticipants] = useState([])

  const [isDownload, setIsDownload] = useState(false)
  const [firstRows, setFirstRows] = useState(0)
  const [totalRows, setTotalRows] = useState(0)

  const [deleteProject, { isLoading: isDeleting }] = useDeleteProjectMutation()
  const [updateProjectMenu, { isLoading: isClosing }] =
    useUpdateProjectMenuMutation()
  const [updateProjectMenuShared, { isLoading: isClosingShared }] =
    useUpdateProjectMenuSharedMutation()
  const [triggerFetchAllProjects] = useLazyFetchProjectsListQuery()
  const [triggerFetchAllSharedProjects] = useLazyGetSharedProjectQuery()

  const isShared = activeTab === 'shared'

  // NEW COLUMNS
  const columns = [
    {
      name: <span>Tanggal buat</span>,
      selector: 'createdAt',
      sortable: true,
      sortField: 'createdAt',
      cell: (row) => {
        return (
          <span>
            {row?.createdAt ? formatDateOnlyPMBV1(row?.createdAt) : '-'}
          </span>
        )
      },
      minWidth: '150px',
      isFreeze: true,
      freezePosition: 'left',
    },
    {
      name: <span>Nomor</span>,
      selector: 'label',
      sortable: true,
      sortField: 'projectLabel',
      cell: (row) => {
        return <span>{row?.label ?? '-'}</span>
      },
      minWidth: '100px',
    },
    {
      name: <span>Judul</span>,
      selector: 'title',
      cell: (row) => {
        return <span>{row?.title ?? '-'}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>Penerima Tugas</span>,
      selector: 'assigneeUser',
      // sortable: true,
      sortField: 'assigneeUser',
      grow: 2,
      cell: (row) => {
        return <span>{row?.assigneeUser ?? ''}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>Induk Project</span>,
      selector: 'parentProject',
      // sortable: true,
      sortField: 'parentProject',
      grow: 2,
      cell: (row) => {
        return (
          <div className="d-flex flex-column">
            <span>{row?.parentProject?.label ?? ''}</span>
            <span className="font-size-sm text-muted">
              {row?.parentProject?.title ?? ''}
            </span>
          </div>
        )
      },
      minWidth: '150px',
    },
    {
      name: <span>Kepemilikan</span>,
      selector: 'name',
      cell: (row) => {
        return <span>{row?.name ?? '-'}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>Target</span>,
      selector: 'goals',
      grow: 2,
      cell: (row) => {
        const goals = row?.goals?.split(',') || []
        const visibleGoals = goals?.slice(0, 3)
        const remainingGoals = goals?.slice(3)
        const remainingGoalsNames = remainingGoals?.join(', ')

        return (
          <div className="d-flex align-items-center gap-2 flex-wrap py-2">
            {visibleGoals?.map((goal, i) => (
              <PMBBadge key={i} className="px-2 w-auto">
                {goal}
              </PMBBadge>
            ))}

            {remainingGoals.length > 0 && (
              <>
                <Badge
                  id={`remaining-goals-tooltip-${row.id}`}
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    cursor: 'pointer',
                    fontSize: '10px',
                    padding: '6px',
                  }}
                >
                  +{remainingGoals.length}
                </Badge>
                <PMBTooltip
                  target={`remaining-goals-tooltip-${row.id}`}
                  placement="top"
                  text={remainingGoalsNames || 'Other goals'}
                />
              </>
            )}
          </div>
        )
      },
      minWidth: '150px',
    },
    {
      name: (
        <div className="d-flex align-items-center gap-1">
          <span>Goals (Unit)</span>
          <span id={`info-goals`} style={{ cursor: 'pointer' }}>
            <Info size={14} />
          </span>
          <PMBTooltip
            target={`info-goals`}
            text="Satuan (unit) dapat berupa jenis yang sama maupun campuran bergantung pada daftar barang yang ada"
          />
        </div>
      ),
      selector: 'goals_summary',
      right: true,
      cell: (row) => {
        return <span>{newFormatThousandSeparator(row?.totalGoal || 0)}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>Completion</span>,
      selector: 'completion_summary',
      right: true,
      cell: (row) => {
        return <span>{newFormatThousandSeparator(row?.totalCompletion || 0)}</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>Percentage</span>,
      selector: 'percentage_summary',
      right: true,
      cell: (row) => {
        return <span>{newFormatThousandSeparator(row?.pctgVsGoal || 0)}%</span>
      },
      minWidth: '150px',
    },
    {
      name: <span>Status</span>,
      selector: 'status',
      center: true,
      cell: (row) => {
        const status = row?.status
        return (
          <PMBBadge
            variant={
              status === 'DRAFT'
                ? 'warning'
                : status === 'ACTIVE'
                  ? 'verify'
                  : 'danger'
            }
            title={capitalizeEveryWord(row?.status)}
            className="px-2 w-auto"
            size="small"
          />
        )
      },
      minWidth: '150px',
    },
    {
      name: '',
      wrap: true,
      center: true,
      minWidth: '200px',
      maxWidth: '200px',
      gap: 2,
      cell: (row) => {
        const isDraft = row?.status === 'DRAFT'
        if (sharedTab) {
          return (
            <SharedProjectActions
              row={row}
              navigate={navigate}
              onClose={(row) => {
                setCloseProjectId(row?.id)
                setCloseProjectLabel(row?.label || '')
                setCloseProjectRow(row)
                setIsOpenClose(true)
                fetchSharedProjectById({ id: row?.id })
              }}
            />
          )
        }
        return (
          <div
            className="d-flex gap-2 align-items-center"
            data-testid={`project-table-row-${row?.id}`}
          >
            <LihatDetail
              onClick={() => {
                navigate(`${URL_PROJECTS_MENU}/${row?.id}`)
              }}
              id={row?.id}
            />

            <ResponsiveActionButton
              key="bagikan"
              iconName="share-2"
              label="Bagikan"
              onClick={() => {
                setShareProject(row)
                setShareParticipants(
                  Array.isArray(row?.participants) ? row?.participants : [],
                )
                setIsOpenShare(true)
              }}
            />
            {/* <ResponsiveActionButton
              disabled={isDraft}
              key="lihat-summary"
              iconName="finance"
              label="Lihat Ringkasan"
              onClick={() => {
                navigate(`${URL_PROJECTS_MENU}/${row?.id}?tab=summary`)
              }}
            /> */}
            <ResponsiveActionButton
              disabled={!isDraft && row?.status !== 'ACTIVE'}
              iconName="pencil"
              label="Ubah"
              onClick={() => {
                navigate(
                  `/projects/edit/${row?.id}` +
                  (isShared ? '?shared=true' : ''),
                )
              }}
            />
            <ResponsiveActionButton
              disabled={!isDraft}
              key="delete"
              iconName="trash"
              label="Hapus"
              onClick={() => {
                setDeleteProjectId(row?.id)
                setDeleteProjectLabel(row?.label || '')
                setIsOpenDelete(true)
              }}
            />
            <ResponsiveActionButton
              disabled={isDraft || row?.status === 'CLOSED'}
              key="close"
              iconName="x"
              label="Tutup"
              onClick={() => {
                setCloseProjectId(row?.id)
                setCloseProjectLabel(row?.label || '')
                setCloseProjectRow(row)
                setIsOpenClose(true)
                fetchProjectById({ projectId: row?.id })
              }}
            />
          </div>
        )
      },
      isFreeze: true,
      freezePosition: 'right',
    },
  ]

  const handleSort = (column, direction) => {
    dispatchActiveTag({
      sortby: column.sortField,
      sortdir: direction?.toUpperCase() || 'DESC',
    })
  }

  useEffect(() => {
    dispatchActiveTag({ page: 0 })
  }, [dispatch, searchInput])

  const debouncedSearch = useDebounce((value) => {
    dispatchActiveTag({
      page: 0,
      s: value,
    })
  }, 600)

  const debouncedGoalMin = useDebounce((value) => {
    dispatchActiveTag({
      page: 0,
      goalMin: value !== '' ? value : undefined,
    })
  }, 600)

  const debouncedGoalMax = useDebounce((value) => {
    dispatchActiveTag({
      page: 0,
      goalMax: value !== '' ? value : undefined,
    })
  }, 600)

  const debouncedCompletionMin = useDebounce((value) => {
    dispatchActiveTag({
      page: 0,
      completionMin: value !== '' ? value : undefined,
    })
  }, 600)

  const debouncedCompletionMax = useDebounce((value) => {
    dispatchActiveTag({
      page: 0,
      completionMax: value !== '' ? value : undefined,
    })
  }, 600)

  const onHandleSearchChange = (value) => {
    setSearchInput(value ?? '')
    debouncedSearch(value ?? '')
  }

  const handleKeyDown = (e) => {
    if (e.key === ' ' && activeTag?.s?.length === 0) {
      e.preventDefault()
    }
  }

  const handleDelete = async () => {
    try {
      await deleteProject({ projectId: deleteProjectId }).unwrap()
      enqueueSnackbar(`Project ${deleteProjectLabel} berhasil dihapus.`, {
        variant: 'success',
      })
      setIsOpenDelete(false)
      setDeleteErrMsg('')
      refetch()
    } catch (error) {
      setDeleteErrMsg(
        error?.data?.meta?.message ||
        error?.data?.message ||
        'Terjadi kesalahan saat menghapus project',
      )
    }
  }

  const handleClose = async () => {
    try {
      const detailRes = activeTab === 'shared' ? sharedRes : res
      const closePayload = {
        ...detailRes?.data,
        status: 'CLOSED',
        participants: detailRes?.data?.participants
          ? detailRes?.data?.participants
          : [],
        goals: detailRes?.data?.goals ? detailRes?.data?.goals : [],
      }

      if (activeTab === 'shared') {
        await updateProjectMenuShared({
          projectId: closeProjectId,
          payload: closePayload,
        }).unwrap()
      } else {
        await updateProjectMenu({
          projectId: closeProjectId,
          payload: closePayload,
        }).unwrap()
      }

      enqueueSnackbar(`Project ${closeProjectLabel} berhasil ditutup.`, {
        variant: 'success',
      })
      setIsOpenClose(false)
      setCloseErrMsg('')
      setCloseProjectRow(null)
      refetch()
    } catch (error) {
      setCloseErrMsg(
        error?.data?.meta?.message ||
        error?.data?.message ||
        'Terjadi kesalahan saat menutup project',
      )
    }
  }

  const handleDownload = () => {
    // If not totalItems, fallback to 10
    const fetchSize = data?.totalItems || 10
    const triggerFn =
      activeTab === 'shared'
        ? triggerFetchAllSharedProjects
        : triggerFetchAllProjects
    triggerFn({
      sortby: activeTag?.sortby,
      sortdir: activeTag?.sortdir,
      search: activeTag?.s,
      page: 0,
      size: fetchSize,
      status: activeTag?.status,
      from: activeTag?.from,
      to: activeTag?.to,
      workerUserIds: activeTag?.workerUserIds,
      targetIds: activeTag?.targetIds,
      goal: activeTag?.goal,
      goalMin: activeTag?.goalMin,
      goalMax: activeTag?.goalMax,
      completion: activeTag?.completion,
      completionMin: activeTag?.completionMin,
      completionMax: activeTag?.completionMax,
      completion_progress: activeTag?.completion_progress,
    })
      .unwrap()
      .then((res) => {
        const fetchedItems = res?.items || []
        handleDownloadExcel(
          fetchedItems,
          fetchSize,
          setIsDownload,
          setFirstRows,
          setTotalRows,
          columns,
          'List',
        )
      })
      .catch((err) => {
        enqueueSnackbar('Terjadi kesalahan saat mengunduh data.', {
          variant: 'error',
        })
      })
  }

  if (isLoading || isFetching) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      <PMBProgressBarExportFile
        isDialogShow={isDownload}
        firstRows={firstRows}
        totalRows={totalRows}
        onCancel={() => setIsDownload(false)}
      />
      <div className="mt-3">
        <BreadCrumbs breadCrumbActive="Daftar Projects" />
      </div>

      {/* MODAL DELETE */}
      <ModalConfirmDelete
        isOpen={isOpenDelete}
        toggle={() => {
          setIsOpenDelete(false)
          setDeleteErrMsg('')
        }}
        projectLabel={deleteProjectLabel}
        onDelete={handleDelete}
        isLoading={isDeleting}
        errMsg={deleteErrMsg}
      />

      {/* MODAL SHARE */}
      <ModalShareProject
        isOpen={isOpenShare}
        toggle={() => setIsOpenShare(false)}
        project={shareProject}
        existingParticipants={shareParticipants}
        refetch={refetch}
      />

      {/* MODAL CLOSE */}
      <ModalConfirmClose
        isOpen={isOpenClose}
        toggle={() => {
          setIsOpenClose(false)
          setCloseErrMsg('')
        }}
        projectLabel={closeProjectLabel}
        onClose={handleClose}
        isLoading={activeTab === 'shared' ? isClosingShared : isClosing}
        errMsg={closeErrMsg}
        isDisabling={
          activeTab === 'shared'
            ? sharedRes?.status === 'pending' || sharedRes?.isFetching
            : res?.status === 'pending' || res?.isFetching
        }
      />

      <Card className="mb-3 border-0 shadow-sm">
        <>
          <div className="row align-items-center py-3 mx-1">
            <div className="col-12 col-md-6 mb-2 mb-md-0 d-flex align-items-center justify-content-start">
              <h5 className="fw-semibold mb-0">Daftar Projects</h5>
            </div>

            <div className="col-12 col-lg-6 d-flex flex-column flex-md-row justify-content-md-end gap-2">
              <CustomButton
                color="primary"
                outline
                className="d-flex align-items-center justify-content-center w-md-auto text-nowrap mb-2 mb-md-0 mt-2"
                onClick={handleDownload}
                disabled={data?.totalItems < 1 || isFetching}
              >
                <Download size={16} className="me-1" />
                Download
              </CustomButton>
              {!isShared && <CustomButton
                color="primary"
                className="d-flex align-items-center justify-content-center w-md-auto text-nowrap mb-2 mb-md-0 mt-2"
                onClick={() => navigate(URL_PROJECTS_ADD)}
                testId="project-add-button"
              >
                <Plus size={16} className="me-1" />
                Tambah Project
              </CustomButton>}
            </div>
          </div>

          <div className="px-3 mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <PMBTabSwitcher
              activeTab={activeTab}
              tabs={tabs}
              onTabChange={handleTabChange}
              className="d-flex gap-3"
            />

            <div className="d-flex align-items-center gap-2">
              {activeFilterCount > 0 && (
                <CustomButton
                  onClick={handleResetFilter}
                  color="danger"
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    padding: 0,
                    minWidth: '40px',
                  }}
                  testId="project-reset-filter-button"
                >
                  <SvgIcon name="x" size={16} color="white" />
                </CustomButton>
              )}

              <CustomButton
                onClick={() => setIsShowFilter((prev) => !prev)}
                color="primary"
                outline
                className="position-relative"
                testId="project-filter-button"
              >
                <div className="d-flex align-items-center gap-2 justify-content-center">
                  <Filter size={15} />
                  <P>Filter</P>
                  {activeFilterCount > 0 && (
                    <Badge
                      color="danger"
                      pill
                      style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        fontSize: '10px',
                      }}
                    >
                      {activeFilterCount}
                    </Badge>
                  )}
                  {isShowFilter ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </div>
              </CustomButton>
            </div>
          </div>

          <div className="px-3">
            <Collapse isOpen={isShowFilter}>
              <div className="border rounded p-3 bg-light mb-3">
                <Row className="g-2">
                  <Col xs={12} md={4} lg={3} className="mb-2">
                    <PMBSearchInputGroup
                      label="Cari"
                      value={searchInput}
                      placeholder="Cari..."
                      maxWidth="100%"
                      onChange={(e) => onHandleSearchChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onClear={() => {
                        setSearchInput('')
                        dispatchActiveTag({ s: undefined, page: 0 })
                      }}
                      testId="project-search-input"
                    />
                  </Col>

                  <Col xs={12} md="auto" lg="auto" className="mb-2">
                    <label className="d-block mb-1">Tanggal Buat</label>
                    <PMBDateRangePicker
                      style={{ width: '100%', maxWidth: '100%' }}
                      value={[
                        activeTag?.from
                          ? moment(activeTag.from).format('YYYY-MM-DD')
                          : null,
                        activeTag?.to
                          ? moment(activeTag.to).format('YYYY-MM-DD')
                          : null,
                      ]}
                      onChange={(dates) => {
                        if (dates && dates.length === 2) {
                          const [startStr, endStr] = dates
                          dispatchActiveTag({
                            from: moment(startStr).startOf('day').toISOString(),
                            to: moment(endStr).endOf('day').toISOString(),
                            page: 0,
                          })
                        } else if (dates === null) {
                          dispatchActiveTag({
                            from: undefined,
                            to: undefined,
                            page: 0,
                          })
                        }
                      }}
                      onClear={() => {
                        dispatchActiveTag({
                          from: undefined,
                          to: undefined,
                          page: 0,
                        })
                      }}
                      format="yyyy-mm-dd"
                      label=""
                      data-testid="project-date-filter"
                    />
                  </Col>

                  <Col xs={12} md={4} lg={2} className="mb-2">
                    <label className="d-block mb-1">Status</label>
                    <PMBSelect
                      style={{ width: '100%' }}
                      value={
                        STATUS_OPTIONS.find(
                          (option) => option.value === activeTag?.status,
                        ) || STATUS_OPTIONS[0]
                      }
                      onChange={(selectedOption) =>
                        dispatchActiveTag({
                          status: selectedOption?.value || undefined,
                          page: 0,
                        })
                      }
                      options={STATUS_OPTIONS}
                      testId="project-status-filter"
                    />
                  </Col>

                  <Col xs={12} md={4} lg={3} className="mb-2">
                    <label className="d-block mb-1">Penerima Tugas</label>
                    <PMBSelect
                      isMulti
                      style={{ width: '100%' }}
                      value={selectedWorkers}
                      onChange={(selected) => {
                        dispatchActiveTag({
                          workerUserIds:
                            selected && selected.length > 0
                              ? selected.map((s) => s.value)
                              : undefined,
                          page: 0,
                        })
                      }}
                      options={memberOptions}
                      isLoading={isLoadingMember}
                      placeholder="Pilih penerima tugas..."
                      testId="project-worker-filter"
                    />
                  </Col>

                  <Col xs={12} md={6} lg={3} className="mb-2">
                    <label className="d-block mb-1">Target</label>
                    <PMBSelect
                      isMulti
                      style={{ width: '100%' }}
                      value={selectedTargets}
                      onChange={(selected) => {
                        dispatchActiveTag({
                          targetIds:
                            selected && selected.length > 0
                              ? selected.map((s) => s.value)
                              : undefined,
                          page: 0,
                        })
                      }}
                      options={targetOptions}
                      placeholder="Pilih target..."
                      testId="project-target-filter"
                    />
                  </Col>

                  <Col xs={12} md={6} lg={3} className="mb-2">
                    <label className="d-block mb-1">Goals</label>
                    <div className="d-flex align-items-center gap-1">
                      <Input
                        type="number"
                        className="form-control"
                        placeholder=">= Min"
                        value={goalMinInput}
                        onChange={(e) => {
                          setGoalMinInput(e.target.value)
                          debouncedGoalMin(e.target.value)
                        }}
                        data-testid="project-goal-min-filter"
                      />
                      <span className="text-muted fw-bold px-1">-</span>
                      <Input
                        type="number"
                        className="form-control"
                        placeholder="<= Maks"
                        value={goalMaxInput}
                        onChange={(e) => {
                          setGoalMaxInput(e.target.value)
                          debouncedGoalMax(e.target.value)
                        }}
                        data-testid="project-goal-max-filter"
                      />
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={3} className="mb-2">
                    <label className="d-block mb-1">Completion</label>
                    <div className="d-flex align-items-center gap-1">
                      <Input
                        type="number"
                        className="form-control"
                        placeholder=">= Min"
                        value={completionMinInput}
                        onChange={(e) => {
                          setCompletionMinInput(e.target.value)
                          debouncedCompletionMin(e.target.value)
                        }}
                        data-testid="project-completion-min-filter"
                      />
                      <span className="text-muted fw-bold px-1">-</span>
                      <Input
                        type="number"
                        className="form-control"
                        placeholder="<= Maks"
                        value={completionMaxInput}
                        onChange={(e) => {
                          setCompletionMaxInput(e.target.value)
                          debouncedCompletionMax(e.target.value)
                        }}
                        data-testid="project-completion-max-filter"
                      />
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={3} className="mb-2">
                    <label className="d-block mb-1">Progres Penyelesaian</label>
                    <PMBSelect
                      style={{ width: '100%' }}
                      value={
                        COMPLETION_PROGRESS_OPTIONS.find(
                          (option) =>
                            option.value !== undefined &&
                            String(option.value) ===
                            String(activeTag?.completion_progress),
                        ) || COMPLETION_PROGRESS_OPTIONS[0]
                      }
                      onChange={(selectedOption) =>
                        dispatchActiveTag({
                          completion_progress:
                            selectedOption?.value ?? undefined,
                          page: 0,
                        })
                      }
                      options={COMPLETION_PROGRESS_OPTIONS}
                      testId="project-completion-progress-filter"
                    />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </div>
        </>

        <CardBody>
          <DataTable
            key={activeTab}
            responsive
            persistTableHead
            columns={columns}
            data={data?.items || []}
            // progressPending={isFetching}
            paginationTotalRows={data?.totalItems}
            pagination
            paginationServer
            paginationComponent={() => (
              <PMBPagination
                rowCount={data?.totalItems || 0}
                currentPage={activeTag?.page}
                rowsPerPage={activeTag?.size}
                setCurrentPage={(e) => {
                  dispatchActiveTag({ page: e })
                }}
                setRowsPerPage={(e) => {
                  dispatchActiveTag({ size: e })
                }}
              />
            )}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner />
              </div>
            }
            onSort={handleSort}
            sortServer
            sortIcon={
              activeTag?.sortdir?.toLowerCase() === 'desc' ? (
                <ChevronDown />
              ) : (
                <ChevronUp />
              )
            }
            noDataComponent={
              <p className="my-3 fw-semibold">
                {activeFilterCount > 0
                  ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                  : activeTab === 'own'
                    ? 'Belum ada project yang Anda buat'
                    : 'Belum ada project yang dibagikan kepada Anda'}
              </p>
            }
          />
        </CardBody>
      </Card>
    </div>
  )
}

export default ListProjectsMenu
